import { ComponentProps, FC, ReactNode } from 'react';

import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';

import { CheckIcon, CloseIcon } from '@/assets/icons';
import { Checkbox, Switch } from '@/components/atoms';
import { cn } from '@/utils/tailwind';

export type DropdownMenuOption = {
  label: string;
  value: string;
  [key: string]: any;
};

type DropdownProps = ComponentProps<typeof DropdownMenuPrimitive.Root> & {
  type?: 'single' | 'multiple';
};
export type DropdownMenuTriggerProps = ComponentProps<typeof DropdownMenuPrimitive.Trigger> & {
  variant?: 'default' | 'transparent';
};
type DropdownMenuContentProps = ComponentProps<typeof DropdownMenuPrimitive.Content>;
type DropdownMenuSeparatorProps = ComponentProps<typeof DropdownMenuPrimitive.Separator>;

type DropdownMenuRadioItemProps = ComponentProps<'div'> & {
  onCheckedChange: (isChecked: boolean) => void;
  className?: string;
  children: ReactNode;
  checked?: boolean;
};
type DropdownMenuCheckBoxProps = ComponentProps<'div'> & {
  onCheckedChange: (isChecked: boolean) => void;
  className?: string;
  children: ReactNode;
  checked?: boolean;
};

type DropdownMenuItemProps = ComponentProps<'div'> & {
  variant?: 'checkbox' | 'switch';
  className?: string;
  children: ReactNode;
  checked?: boolean;
  onCheckedChange: (isChecked?: boolean) => void;
};

type DropdownMenuSimpleItem = ComponentProps<'button'>;

type DropdownMenuClearSelectionProps = { withIcon?: boolean } & ComponentProps<'div'>;

const Dropdown = ({ children, ...props }: DropdownProps) => {
  return (
    <DropdownMenuPrimitive.Root data-slot="dropdown-menu" {...props}>
      {children}
    </DropdownMenuPrimitive.Root>
  );
};

const DropdownMenuTrigger = ({
  variant = 'default',
  className,
  ...props
}: DropdownMenuTriggerProps) => {
  return (
    <DropdownMenuPrimitive.Trigger
      data-slot="dropdown-menu-trigger"
      className={cn(
        'ring-0 py-2 ring-transparent !focus-visible:ring-0 focus-visible:ring-transparent focus:bg-transparent shadow-none border-none select-none',
        variant === 'default'
          ? 'bg-custom-warm-grey-25 outline-2 !outline-custom-warm-grey-200 data-[state=open]:!outline-custom-cyan-400'
          : '',
        className
      )}
      {...props}
    />
  );
};

const DropdownMenuContent = ({
  className,
  sideOffset = 8,
  side = 'bottom',
  align = 'end',
  ...props
}: DropdownMenuContentProps) => {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        align={align}
        side={side}
        data-slot="dropdown-menu-content"
        sideOffset={sideOffset}
        className={cn(
          'bg-custom-warm-grey-25 flex flex-col gap-1 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-h-(--radix-dropdown-menu-content-available-height) min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-x-hidden overflow-y-auto border border-custom-warm-grey-200 p-2 rounded-[12px] shadow-custom-dropdown [scrollbar-color:theme(colors.custom-warm-grey-100)_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-[4px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-[16px] [&::-webkit-scrollbar-thumb]:bg-custom-warm-grey-100',
          className
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
};

const DropdownMenuSeparator = ({ className, ...props }: DropdownMenuSeparatorProps) => {
  return (
    <DropdownMenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn('bg-border -mx-2 my-1 h-px', className)}
      {...props}
    />
  );
};

const DropdownMenuRadioItem = ({
  className,
  children,
  checked,
  onCheckedChange,
  ...props
}: DropdownMenuRadioItemProps) => {
  const handleClick = () => {
    if (onCheckedChange) {
      onCheckedChange(!checked);
    }
  };

  return (
    <div
      role="menuitemcheckbox"
      data-slot="dropdown-menu-radio-item"
      data-state={checked ? 'checked' : 'unchecked'}
      className={cn(
        'relative min-w-[180px] bg-transparent flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:shrink-0 !pointer-events-auto',
        className
      )}
      onClick={handleClick}
      {...props}
    >
      <div className="grow flex items-center gap-2 min-h-[20px] text-body-14-regular text-custom-warm-grey-800 [&_svg]:shrink-0">
        {children}
      </div>
      <Switch checked={checked} />
    </div>
  );
};

const DropdownMenuSimpleItem = ({ className, ...props }: DropdownMenuSimpleItem) => {
  return (
    <button
      className={cn(
        'cursor-pointer text-body-14-regular !text-custom-warm-grey-800 px-3 py-2 flex items-center gap-2.5 hover:bg-custom-warm-grey-100 rounded-[8px] w-full',
        className
      )}
      {...props}
    />
  );
};

const DropdownMenuItem: FC<DropdownMenuItemProps> = ({
  className,
  children,
  checked,
  variant = 'checkbox',
  onCheckedChange,
}) => {
  return (
    <div
      role="menuitemcheckbox"
      data-state={checked ? 'checked' : 'unchecked'}
      className={cn(
        'relative min-w-[180px] bg-transparent flex cursor-pointer items-center gap-2.5 rounded-sm px-3 py-2 outline-hidden select-none hover:bg-custom-warm-grey-100',
        { 'bg-custom-warm-grey-100': checked },
        className
      )}
      onClick={e => {
        e.preventDefault();
        onCheckedChange?.(checked);
      }}
    >
      <div className="grow flex items-center gap-2 min-h-[20px] text-body-14-regular text-custom-warm-grey-800">
        {children}
      </div>
      {checked &&
        (variant === 'checkbox' ? (
          <div className="size-5 flex items-center justify-center">
            <CheckIcon className="text-custom-cyan-500" />
          </div>
        ) : (
          <Switch checked={Boolean(checked)} />
        ))}
    </div>
  );
};

const DropdownMenuCheckbox = ({
  className,
  children,
  checked,
  onCheckedChange,
  ...props
}: DropdownMenuCheckBoxProps) => {
  const handleClick = () => {
    onCheckedChange?.(!checked);
  };

  return (
    <div
      role="menuitemcheckbox"
      data-slot="dropdown-menu-radio-item"
      className={cn(
        'relative min-w-[180px] bg-transparent flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2  text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:shrink-0 !pointer-events-auto',
        className
      )}
      data-state={checked ? 'checked' : 'unchecked'}
      onClick={handleClick}
      {...props}
    >
      <Checkbox checked={checked} />
      <div className="grow flex items-center gap-2 min-h-[20px] text-body-14-regular text-custom-warm-grey-800 [&_svg]:shrink-0">
        {children}
      </div>
    </div>
  );
};

const DropdownMenuClearSelection = ({
  className,
  children,
  withIcon = true,
  ...props
}: DropdownMenuClearSelectionProps) => {
  return (
    <div
      data-slot="dropdown-menu-item"
      className={cn(
        'relative min-w-[180px] bg-transparent flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2  text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:shrink-0 !pointer-events-auto',
        className
      )}
      onPointerLeave={e => e.preventDefault()}
      onPointerMove={e => e.preventDefault()}
      {...props}
    >
      {withIcon && <CloseIcon />}
      <div className="grow flex items-center gap-2 min-h-[20px] text-body-14-medium text-custom-warm-grey-800 [&_svg]:shrink-0">
        {children}
      </div>
    </div>
  );
};

export {
  Dropdown,
  DropdownMenuCheckbox,
  DropdownMenuClearSelection,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSimpleItem,
  DropdownMenuTrigger,
};
